import type { ReactNode } from 'react';
import type { Animal, Color } from '../data/looks';

/** Четыре зверя, нарисованных фигурами, а не линиями: ребёнок узнаёт силуэт
 *  с расстояния и на дешёвом экране, а `currentColor` красит его в цвет
 *  профиля. Остальных добавим позже — список закрытый, и каждый новый обязан
 *  получить рисунок здесь же (`tests/ui/looks.test.ts` за этим следит). */
const SHAPES: Record<Animal, ReactNode> = {
  // Ёж: круглая мордочка и колючки веером.
  hedgehog: (
    <>
      <path d="M2 15 6 6l3 7 3-8 3 8 3-7 4 9z" />
      <circle cx="18" cy="17" r="4" />
      <circle cx="20" cy="16" r="1" fill="#fff" />
    </>
  ),
  // Лиса: острые уши, треугольная морда.
  fox: (
    <>
      <path d="M3 4l4 4h10l4-4 1 8a9 7 0 0 1-20 0z" />
      <circle cx="9" cy="11" r="1.2" fill="#fff" />
      <circle cx="15" cy="11" r="1.2" fill="#fff" />
    </>
  ),
  // Кот: круглая голова, уши треугольниками, усы.
  cat: (
    <>
      <path d="M4 5l3 4h10l3-4v6a8 7 0 0 1-16 0z" />
      <circle cx="9" cy="11" r="1.2" fill="#fff" />
      <circle cx="15" cy="11" r="1.2" fill="#fff" />
      <path d="M2 14h5M17 14h5" stroke="currentColor" strokeWidth="1.5" />
    </>
  ),
  // Сова: два больших глаза и кисточки на ушах.
  owl: (
    <>
      <path d="M4 6l2-3 2 3h8l2-3 2 3v8a8 8 0 0 1-16 0z" />
      <circle cx="9" cy="11" r="3" fill="#fff" />
      <circle cx="15" cy="11" r="3" fill="#fff" />
      <circle cx="9" cy="11" r="1.2" />
      <circle cx="15" cy="11" r="1.2" />
    </>
  ),
};

/** Классы цвета отдельной таблицей, а не строкой в шаблоне: Tailwind ищет
 *  имена классов по исходному тексту и собранное на лету `text-${color}`
 *  просто не попадёт в стили. */
const COLOR_CLASS: Record<Color, string> = {
  blue: 'text-sky-500',
  green: 'text-emerald-500',
  amber: 'text-amber-500',
  rose: 'text-rose-500',
  violet: 'text-violet-500',
  teal: 'text-teal-500',
};

export function AnimalIcon({
  animal,
  color,
  size = 48,
}: {
  animal: Animal;
  color: Color;
  size?: number;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      className={COLOR_CLASS[color]}
      role="img"
      aria-label={LABELS[animal]}
    >
      {SHAPES[animal]}
    </svg>
  );
}

/** Подпись для тех, кто не видит картинку. Единственное место, где у зверя
 *  есть русское имя. */
export const LABELS: Record<Animal, string> = {
  hedgehog: 'ёж',
  fox: 'лиса',
  cat: 'кот',
  owl: 'сова',
};

export const COLOR_LABELS: Record<Color, string> = {
  blue: 'синий',
  green: 'зелёный',
  amber: 'жёлтый',
  rose: 'розовый',
  violet: 'фиолетовый',
  teal: 'бирюзовый',
};

export { COLOR_CLASS };
